import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './Chatbot.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { retryMessage, selectChatbot, sendMessage } from '../store/Chatbot.store';
import { ReactComponent as RepeatMessageIcon } from '../assets/svgs/chat-repeat-message.svg';
import { ReactComponent as LinkIcon } from '../assets/svgs/link-45deg.svg';
import { Link } from 'react-router-dom';

const ChatBody: React.FC = () => {
    const { messages, isLoading, isError, mostRecentMessage, sessionId, followUpPrompts } = useSelector(selectChatbot);
    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        const chatContainer = chatContainerRef.current;

        const handleScroll = (event: WheelEvent) => {
            if (chatContainer) {
                const { scrollTop, scrollHeight, clientHeight } = chatContainer;
                const isAtTop = scrollTop === 0;
                const isAtBottom = scrollTop + clientHeight === scrollHeight;

                if ((isAtTop && event.deltaY < 0) || (isAtBottom && event.deltaY > 0)) {
                    event.preventDefault();
                }
            }
        };

        if (chatContainer) {
            chatContainer.addEventListener('wheel', handleScroll);
        }

        return () => {
            if (chatContainer) {
                chatContainer.removeEventListener('wheel', handleScroll);
            }
        };
    }, [messages]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const handleRetryMessage = () => {
        dispatch(
            retryMessage({
                message: mostRecentMessage!,
                messages: messages,
                sessionId: sessionId!,
            })
        );
    };

    const handleBubbleClick = (prompt: string) => {
        dispatch(sendMessage({ message: prompt, messages: messages, sessionId: sessionId! }));
    };

    return (
        <div ref={chatContainerRef} className={styles.chatBody}>
            <div className={styles.disclaimerContainer}>
                {'Bitte geben Sie keine persönlichen Daten ein. Überprüfen Sie außerdem die Antworten, da die Ergebnisse ' +
                    'möglicherweise nicht immer korrekt sind.'}
            </div>
            {messages.map((msg, index) => (
                <div key={index} className={`${styles.messageContainer} ${msg.sender === 'user' ? styles.user : ''}`}>
                    <div className={`${styles.message} ${msg.sender === 'user' ? styles.user : ''}`}>
                        {msg.sender === 'user' ? (
                            <p>{msg.message}</p>
                        ) : (
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: ({ href, children, ...props }) => {
                                        // Prüfe, ob es ein interner Link ist (z.B. beginnt mit '/')
                                        if (href && href.startsWith('/')) {
                                            return (
                                                <>
                                                    <Link to={href} {...props}>
                                                        {children}
                                                    </Link>
                                                    <LinkIcon className={styles.chatBotLinkIcon}></LinkIcon>
                                                </>
                                            );
                                        }
                                        // Externe Links wie gewohnt behandeln
                                        return (
                                            <a href={href} {...props} target="_blank" rel="noopener noreferrer">
                                                {children}
                                            </a>
                                        );
                                    },
                                }}
                            >
                                {msg.message}
                            </ReactMarkdown>
                        )}
                    </div>
                </div>
            ))}
            {!isLoading &&
                !isError &&
                followUpPrompts &&
                followUpPrompts.map((prompt, index) => (
                    <button key={index} className={styles.bubble} onClick={() => handleBubbleClick(prompt)}>
                        {prompt}
                    </button>
                ))}{' '}
            {isLoading && (
                <div className={styles.messageContainer}>
                    <div className={`${styles.loadingDots} ${styles.message}`}>
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </div>
                </div>
            )}
            {isError && (
                <div className={`${styles.messageContainer}`}>
                    <div className={`${styles.message} ${styles.repeatMessage}`}>
                        <div>Fehler beim Senden der Nachricht. Klicken Sie hier, um die Nachricht erneut zu senden.</div>
                        <button className={styles.retryButton} onClick={handleRetryMessage}>
                            <RepeatMessageIcon className={styles.chatBotIcon}></RepeatMessageIcon>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatBody;
